import { useEffect, useState, useCallback } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

/**
 * Hook to synchronize app theme with Microsoft Teams theme.
 * Returns the Teams theme and a handler for theme changes.
 */
export function useTeamsTheme(setDarkMode: (dark: boolean) => void) {
  const [isInTeams, setIsInTeams] = useState(false);
  const [teamsTheme, setTeamsTheme] = useState<string | null>(null);

  const handleThemeChange = useCallback((theme: string) => {
    setTeamsTheme(theme);
    // Teams themes: 'default' (light), 'dark', 'contrast' (high contrast)
    setDarkMode(theme === 'dark' || theme === 'contrast');
  }, [setDarkMode]);

  useEffect(() => {
    // Check if we're running inside Teams
    const inTeams = window.parent !== window || window.name === 'embedded-page-container';

    if (!inTeams) {
      return;
    }

    // Try to get Teams context
    microsoftTeams.app.getContext()
      .then((context) => {
        setIsInTeams(true);
        if (context.app?.theme) {
          handleThemeChange(context.app.theme);
        }

        // Register for theme changes
        microsoftTeams.app.registerOnThemeChangeHandler(handleThemeChange);
      })
      .catch(() => {
        // Not in Teams context or SDK not initialized
        setIsInTeams(false);
      });

    // Cleanup: no explicit unregister needed as SDK handles it
  }, [handleThemeChange]);

  return { isInTeams, teamsTheme };
}
